import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useContent } from '@/content'
import { cn } from '@/lib/cn'
import { ROUTES } from '@/lib/paths'
import { Icon } from '@/components/ui'
import { SoundToggle } from '@/features/audio'
import styles from './NavRail.module.css'

/** The arc the items fan across, and where it starts. */
const ARC_START = 180 // pointing left
const ARC_SWEEP = 180 // through straight up, round to pointing right

/**
 * The site's primary navigation.
 *
 * Two shapes from one markup, chosen by media query:
 *
 * - **Pointer, wide screen** — a lateral icon rail pinned to the left edge,
 *   every item visible at once, each revealing its label on hover.
 * - **Phone** — a single round button at the bottom; tapping it fans the
 *   items out in an arc above it. Eight controls across a 390px screen is a
 *   row of 41px targets crammed edge to edge, and it still leaves nothing
 *   for the page. A fan gives each item a full-size target and gives the
 *   page its bottom edge back.
 *
 * Icons carry no accessible name on their own, so each item keeps a real
 * text label — shown on hover for pointers, and exposed to assistive
 * technology at every size via `aria-label`.
 */
export const NavRail = () => {
  const { navItems: NAV_ITEMS, site } = useContent()
  const [open, setOpen] = useState(false)
  const nav = useRef<HTMLElement>(null)
  const { pathname } = useLocation()

  // Arriving somewhere new is the end of navigating; the fan should not
  // still be hanging over the page that was just chosen.
  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onOutside = (event: PointerEvent) => {
      const node = event.target
      if (node instanceof Node && !nav.current?.contains(node)) setOpen(false)
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onOutside)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onOutside)
    }
  }, [open])

  /**
   * Places one item on the arc.
   *
   * The angle is the item's share of the sweep; the radius and the stagger
   * come from CSS, so the fan can be resized without touching this. The
   * trigonometry is left to CSS too — `cos()` and `sin()` in `calc()` have
   * been available everywhere since early 2023, and keeping it there means
   * the radius can respond to the viewport.
   */
  const place = (index: number, total: number): CSSProperties =>
    ({
      '--angle': `${ARC_START + (index * ARC_SWEEP) / Math.max(1, total - 1)}deg`,
      // Outermost items start last on the way out and first on the way back,
      // so the fan reads as one gesture rather than eight.
      '--delay': `${index * 35}ms`,
    }) as CSSProperties

  // Resume and the sound switch sit on the arc beside the links, so the fan
  // is the whole of the navigation rather than most of it.
  const extras: { key: string; label: string; node: ReactNode }[] = [
    {
      key: 'resume',
      label: 'Download CV',
      node: (
        <a
          href={site.resume}
          download
          aria-label="Download CV"
          className={styles.resume}
        >
          <Icon name="download" size={20} />
        </a>
      ),
    },
    { key: 'sound', label: 'Sound', node: <SoundToggle /> },
  ]

  const total = NAV_ITEMS.length + extras.length

  return (
    <nav
      className={cn(styles.rail, open && styles.railOpen)}
      aria-label="Primary"
      ref={nav}
      data-open={open ? 'true' : 'false'}
    >
      {/*
        * A scrim behind the open fan.
        *
        * The items are dark circles on a dark page, so without it they sit
        * in the middle of whatever paragraph happens to be behind them and
        * the whole menu is hard to read. It is also the tap-to-close target,
        * which is what people reach for first.
        */}
      <button
        type="button"
        className={styles.scrim}
        aria-label="Close navigation"
        tabIndex={open ? 0 : -1}
        onClick={() => setOpen(false)}
      />

      <ul className={styles.list}>
        {NAV_ITEMS.map((item, index) => (
          <li
            key={item.path}
            className={styles.item}
            style={place(index, total)}
          >
            <NavLink
              to={item.path}
              end={item.path === ROUTES.home}
              aria-label={item.label}
              className={({ isActive }) =>
                cn(styles.link, isActive && styles.linkActive)
              }
            >
              <Icon name={item.icon} size={20} />
            </NavLink>
            <span className={styles.label} aria-hidden="true">
              {item.label}
            </span>
          </li>
        ))}
      </ul>

      <span className={styles.divider} aria-hidden="true" />

      {extras.map((extra, index) => (
        <div
          key={extra.key}
          className={styles.item}
          style={place(NAV_ITEMS.length + index, total)}
        >
          {extra.node}
          <span className={styles.label} aria-hidden="true">
            {extra.label}
          </span>
        </div>
      ))}

      {/*
        * The trigger. Hidden on a pointer, where every item is already
        * visible and a button that opens what is open would be nonsense.
        */}
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        aria-label={open ? 'Close navigation' : 'Open navigation'}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="plus" size={22} />
      </button>
    </nav>
  )
}
